import { supabase } from './supabase';
import { setFeature, type FeatureKey, type TenantFeatures } from './superAdmin';

/**
 * Persistance des activations d'options par resto (US-512). Table `tenant_features`
 * (tenant_id, feature_key, enabled). Super admin active/désactive ; chaque resto lit
 * ses propres flags. RÉSILIENT : si la table n'existe pas encore (migration non lancée),
 * on ne casse pas l'UI (lecture vide, écriture loggée).
 */

/** Charge tous les flags : { tenantId → { featureKey → bool } }. */
export async function loadAllTenantFeatures(): Promise<Record<string, TenantFeatures>> {
  const { data, error } = await supabase.from('tenant_features').select('tenant_id, feature_key, enabled');
  if (error || !Array.isArray(data)) {
    if (error) console.warn('[features] lecture tenant_features impossible:', error.message);
    return {};
  }
  const out: Record<string, TenantFeatures> = {};
  for (const row of data as { tenant_id: string; feature_key: string; enabled: boolean }[]) {
    const cur = out[row.tenant_id] ?? {};
    out[row.tenant_id] = setFeature(cur, row.feature_key as FeatureKey, row.enabled === true);
  }
  return out;
}

/** Charge les flags d'un seul resto. */
export async function loadTenantFeatures(tenantId: string): Promise<TenantFeatures> {
  const { data, error } = await supabase
    .from('tenant_features')
    .select('feature_key, enabled')
    .eq('tenant_id', tenantId);
  if (error || !Array.isArray(data)) return {};
  let f: TenantFeatures = {};
  for (const row of data as { feature_key: string; enabled: boolean }[]) {
    f = setFeature(f, row.feature_key as FeatureKey, row.enabled === true);
  }
  return f;
}

/**
 * SUPER ADMIN — active/désactive une option pour N'IMPORTE QUEL resto, via la RPC
 * sécurisée `admin_set_tenant_feature` (SECURITY DEFINER super_admin, cf. sql/33).
 * À utiliser depuis le super admin (le upsert direct est bloqué par la RLS car le
 * super admin n'est pas propriétaire du resto). Renvoie true si persisté.
 */
export async function adminSetTenantFeature(tenantId: string, key: FeatureKey, enabled: boolean): Promise<boolean> {
  const { error } = await supabase.rpc('admin_set_tenant_feature', {
    p_tenant_id: tenantId,
    p_key: key,
    p_enabled: enabled,
  });
  if (error) {
    console.warn('[features] persistance super admin impossible (sql/33 manquant ?):', error.message);
    return false;
  }
  return true;
}

/** Active/désactive une option pour un resto (upsert). Renvoie true si persisté. */
export async function setTenantFeature(tenantId: string, key: FeatureKey, enabled: boolean): Promise<boolean> {
  const { error } = await supabase
    .from('tenant_features')
    .upsert({ tenant_id: tenantId, feature_key: key, enabled }, { onConflict: 'tenant_id,feature_key' });
  if (error) {
    console.warn('[features] persistance flag impossible (migration manquante ?):', error.message);
    return false;
  }
  return true;
}
